import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Slide, { SlideProps } from '@mui/material/Slide';
import Tooltip from '@mui/material/Tooltip';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { useDispatch } from '@cbridge-utils/react-redux';
import { NotificationAction } from '@cbridge-packages/notification';
import { StyledSnackbarContent } from './StyledSnackbarContent';
import { StyledTextBox } from './StyledTextBox';
import { INotificationProps } from './Notification.interface';

const MessageBox = styled(Box)({
  display: 'flex',
  flex: 1,
  alignItems: 'flex-start',
  minWidth: 0,
  overflow: 'hidden',
});

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction={'left'} />;
}

export const Notification: FunctionComponent<INotificationProps> = (props) => {
  const { notificationKey, open, message, severity, autoHideDuration } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleClose = (_event: unknown, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(NotificationAction.close({ key: notificationKey }));
  };

  const renderIcon = () => {
    if (severity === 'success') {
      return <CheckCircleIcon sx={{ color: 'success.main', mr: 1.5, flexShrink: 0 }} />;
    }
    if (severity === 'error') {
      return <WarningIcon sx={{ color: 'error.main', mr: 1.5, flexShrink: 0 }} />;
    }
    return null;
  };

  const action = (
    <IconButton
      size={'small'}
      aria-label={t('text:common.button.close')}
      title={t('text:common.button.close')}
      color={'inherit'}
      onClick={handleClose}
      sx={{ flexShrink: 0 }}
    >
      <CloseIcon fontSize={'small'} />
    </IconButton>
  );

  const messageContent = (
    <MessageBox>
      {renderIcon()}
      <Tooltip title={message} arrow={true}>
        <StyledTextBox component={'span'}>{message}</StyledTextBox>
      </Tooltip>
    </MessageBox>
  );

  return (
    <Snackbar
      key={notificationKey}
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      slots={{ transition: SlideTransition }}
      transitionDuration={{ enter: 200, exit: 200 }}
      sx={{
        position: 'static',
        transform: 'none',
      }}
    >
      <StyledSnackbarContent message={messageContent} action={action} />
    </Snackbar>
  );
};
